<template>
    <h4 class="mb-2.5">
        請確認您的行動電話及E-mail
    </h4>
    <InputForm
        v-model:isOpen="isOpenMobile"
        :btnText="editable ? '變更' :''"
        class="border-b border-dashed md:border-solid"
        @resetValues="cancelMobileChange"
    >
        <template #title>
            <h4 class="text-base text-gray-900 mb-3 mt-4 md:mt-5">
                行動電話
            </h4>
        </template>
        <template #data>
            <span v-if="showData.mobileCountry && showData.mobileCountry !== '886'">
                {{ `(+${showData.mobileCountry} )` }}
            </span>
            <span v-if="showData.mobile">{{ showData.mobile }}</span>
            <span
                v-else
                class="text-gray-300"
            >尚未留存行動電話</span>
        </template>
        <template #inputs>
            <div class="flex flex-wrap">
                <div class="md:mb-0 mr-2 max-w-[12.5rem] w-[calc(50%-0.25rem)] md:w-[12.5rem]">
                    <Multiselect
                        v-model="userData.mobileCountry"
                        class="select-base"
                        placeholder="國碼"
                        track-by="text"
                        :options="mobileCodeList"
                        :can-clear="false"
                        :can-deselect="false"
                        @select="validateMobile()"
                    />
                    <div class="error-msg text-right">
                        {{ errorMessage.mobileCountry }}
                    </div>
                </div>
                <div class="md:mr-2 w-[calc(50%-0.25rem)] md:w-auto max-w-[12.5rem]">
                    <input
                        v-model.trim="userData.mobile"
                        class="input-base"
                        inputmode="numeric"
                        autocomplete="off"
                        type="text"
                        placeholder="行動電話"
                        :disabled="!userData.mobileCountry"
                        @input="validateMobile()"
                    >
                    <p class="error-msg text-right">
                        {{ errorMessage.mobile }}
                    </p>
                </div>
            </div>
        </template>
    </InputForm>
    <InputForm
        v-model:isOpen="isOpenEmail"
        :btnText="editable ? '變更' :''"
        class="border-b border-dashed md:border-solid"
        @resetValues="cancelEmailChange"
    >
        <template #title>
            <h4 class="text-base text-gray-900 mb-3 mt-4 md:mt-5">
                E-mail
            </h4>
        </template>
        <template #data>
            <div class="break-all md:w-5/6">
                <span v-if="showData.email">{{ showData.email }}</span>
                <span
                    v-else
                    class="text-gray-300"
                >
                    尚未留存E-mail
                </span>
            </div>
        </template>
        <template #inputs>
            <div class="flex flex-col sm:flex-row">
                <div class="sm:w-[calc(100%-7.75rem)] md:w-[52%] md:max-w-[25.1875rem] mb-2 pr-2">
                    <input
                        v-model.trim="userData.email"
                        type="text"
                        placeholder="example@fubon.com"
                        maxlength="264"
                        inputmode="email"
                        autocomplete="off"
                        class="input-base w-full"
                        @input="validateEmail(emailText)"
                    >
                    <p class="error-msg text-right">
                        {{ errorMessage.email }}
                    </p>
                </div>
                <ValidEmail
                    v-model:isValid="isValid"
                    v-model:needEmailVerify="needEmailVerify"
                    :email="userData.email"
                    :isPassCheck="errorMessage?.email===''"
                />
            </div>
        </template>
    </InputForm>
    <ul
        v-if="editable"
        class="text-tip mb-7 mt-4"
    >
        <li>若要變更，請點選變更輸入。</li>
        <li>本次申請變更之要保人行動電話及E-mail，將同時更新以您為要保人所投保之所有個人險有效及停效保單。</li>
    </ul>
    <PopupBase
        v-model:visiblePopup="visiblePopup"
        :params="{
            title: '請調整資料內容',
            confirmButton: '確認',
        }"
        @confirm="closePopup"
    >
        <template #content>
            <ul :class="[catchError.length > 0 && !errorMessage.mobile && !errorMessage.email?'':'list-decimal space-y-2 ml-6']">
                <li v-if="errorMessage.mobile">
                    <p>行動電話</p>
                    <ul>
                        <li>{{ errorMessage.mobile }}</li>
                    </ul>
                </li>
                <li v-if="errorMessage.email">
                    <p>E-mail</p>
                    <ul>
                        <li>{{ errorMessage.email }}</li>
                    </ul>
                </li>
                <li v-if="catchError.length > 0">
                    <ul>
                        <li
                            v-for="(msg, index) in catchError"
                            :key="index"
                        >
                            {{ msg.errorMessage }}
                        </li>
                    </ul>
                </li>
            </ul>
        </template>
    </PopupBase>
    <PopupEmailvalid v-model:isPopup="visiblePopupEmail" />
</template>

<script>
import { ref, reactive, toRefs, inject, watch, watchEffect, toRef } from 'vue';
import { commonValidateEmail } from '@/utils/rules/validateEmail.js';
import { commonValidMobile } from '@/utils/rules/validatePhone.js';
import InputForm from '@/components/collapse/CollapseFrame.vue';
import Multiselect from '@vueform/multiselect';
import PopupBase from '@/components/dialogs/Popup.vue';
import PopupEmailvalid from './PopupEmailvalid.vue';
import ValidEmail from '@/components/dialogs/ValidEmail.vue';
import _ from 'lodash';

export default {
    components: {
        InputForm,
        Multiselect,
        PopupBase,
        PopupEmailvalid,
        ValidEmail,
    },
    props: {
        info: {
            type: Object,
            default: () => { }
        },
        mobileCodeList: {
            type: Array,
            default: () => []
        },
        validInput: {
            type: Boolean,
            default: false
        },
        catchErrorArray: {
            type: Array,
            default: () => []
        },
        editable: {
            type: Boolean,
            default: true
        },
        isDisable: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:validInput', 'update:isDisable', 'getValidResult', 'closePopup', 'clearError'],
    setup(props, { emit }) {
        const $vfm = inject('$vfm');
        const visiblePopupEmail = ref(false);
        const state = reactive({
            isOpenMobile: false,
            isOpenEmail: false,
            visiblePopup: false,
            isValid: false,
            needEmailVerify: true,
        });
        const propsInfo = toRef(props, 'info');

        const data = reactive({
            showData: {},
            userData: {
                mobile: '',
                email: '',
                mobileCountry: ''
            },
            errorMessage: {
                mobile: '',
                email: '',
                mobileCountry: ''
            },
            remindMessage: {
                mobile: '',
                email: ''
            },
            mobileCodeList: [],
            catchError: []
        });

        watch(propsInfo.value, (nV) => {
            data.showData = _.cloneDeep(nV);
            data.userData.email = nV.email;
            data.userData.mobileCountry = nV.mobileCountry;
            data.userData.mobile = nV.mobile;
        }, { immediate: true });

        watch(() => props.validInput, (nV) => {
            if (nV) validateData();
        });

        watch(() => props.catchErrorArray, (nV) => {
            if (nV.length > 0) {
                data.catchError = props.catchErrorArray;
                state.visiblePopup = true;
            }
        });

        watchEffect(() => {
            emit('update:isDisable', state.needEmailVerify && state.isOpenEmail && !state.isValid);
        });

        // 驗證E-mail，@前後是否一樣
        const isEmailSameWithAtSign = () => {
            const showDataEmailArray = data.showData.email.split('@');
            const userDataEmailArray = data.userData.email.split('@');
            const isfrontSame = showDataEmailArray[0] === userDataEmailArray[0];
            const isBackSame = showDataEmailArray[1]?.toLowerCase() === userDataEmailArray[1]?.toLowerCase();
            return isfrontSame && isBackSame;
        };

        const validateMobile = () => {
            const { mobile, mobileCountry } = data.userData;
            data.errorMessage.mobile = commonValidMobile({
                mobileCountry: mobileCountry,
                mobile: mobile,
                isRequired: true
            });
        };

        const validateEmail = () => {
            const { showData, userData } = data;
            // 需要驗證的三種可能:1.沒有email 2.不是保單借款 3.在保單借款，點擊變更，且資料有更改
            if (!showData.email) {
                data.errorMessage.email = commonValidateEmail({ mail: userData.email, isRequired: true });
            }

            if ((state.isOpenEmail && showData.email !== userData.email & !isEmailSameWithAtSign())) {
                data.errorMessage.email = commonValidateEmail({ mail: userData.email, isRequired: true });
            } else {
                data.errorMessage.email = '';
            }
        };

        const cancelMobileChange = () => {
            state.isOpenMobile = false;
            data.userData.mobile = data.showData.mobile;
            data.userData.mobileCountry = data.showData.mobileCountry;
            data.errorMessage.mobile = '';
        };

        const cancelEmailChange = () => {
            state.isOpenEmail = false;
            data.userData.email = data.showData.email;
            data.errorMessage.email = '';
        };

        const validateData = () => {
            const { showData, userData } = data;
            const sameDataItems = [];

            // 只要沒值都要驗證
            if (!showData.mobile) validateMobile();
            if (!showData.email) validateMobile();

            const isMobileSame = showData.mobile === userData.mobile && showData.mobileCountry === userData.mobileCountry;
            if (state.isOpenMobile) {
                if (state.isOpenMobile && isMobileSame) {
                    data.errorMessage.mobile = '';
                    // data.remindMessage.mobile = '行動電話之資料內容未作變更，如無需變更請點選『取消變更』。';
                    sameDataItems.push('行動電話');
                } else {
                    validateMobile();
                }
            }

            const isEmailSame = showData.email === userData.email;
            // 要驗證情況
            // 1. 在借款內
            //    1-1 有開變更，有改資料
            // 2. 不在借款
            //    2-1 有開變更，有改資料
            //    2-1 沒開變更
            // 要出現驗證燈箱
            // 1. 只要有開變更，沒改資料
            if (state.isOpenEmail) {
                if (state.isOpenEmail && (isEmailSame || isEmailSameWithAtSign())) {
                    data.errorMessage.email = '';
                    // data.remindMessage.email = 'E-mail之資料內容未作變更，如無需變更請點選『取消變更』。';
                    sameDataItems.push('E-mail');
                } else {
                    validateEmail();
                }
            }

            const { errorMessage } = data;
            const retrunObj = { isPass: false, returnData: {} };

            if (sameDataItems.length > 0) {
                // 資料相同燈箱
                modalShow(sameDataItems.join('、'));
            } else if (errorMessage.email || errorMessage.mobile || errorMessage.mobileCountry) {
                // 驗證資料燈箱
                state.visiblePopup = true;
            } else {
                retrunObj.isPass = true;
                retrunObj.returnData = data.userData;
            }
            emit('update:validInput', false);
            emit('getValidResult', retrunObj);
        };

        const closePopup = () => {
            state.visiblePopup = false;
            if (props.catchErrorArray.length > 0) {
                data.catchError = [];
                emit('closePopup');
                emit('clearError');
            }
        };

        const modalShow = (item) => {
            $vfm.show({
                component: 'DialogBase',
                params: {
                    title: '請確認',
                    confirmButton: '確認',
                    content: `${item}之資料內容未作變更，如無需變更請點選『取消變更』。`,
                },
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                    },
                },
            });
        };

        return {
            ...toRefs(state),
            ...toRefs(data),
            validateMobile,
            validateEmail,
            cancelMobileChange,
            cancelEmailChange,
            validateData,
            closePopup,
            visiblePopupEmail,
        };
    }
};
</script>

<style scoped>
</style>
